import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowLeft, MapPin, ExternalLink, Mail, Phone, Globe, Facebook, Instagram,
  Clock, Store,
} from "lucide-react";
import { fetchDealerBySlug, DEALER_CATEGORY_LABELS } from "@/lib/dealers.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

const dOptions = (slug: string) =>
  queryOptions({
    queryKey: ["community-dealer", slug],
    queryFn: async () => {
      const d = await fetchDealerBySlug(slug);
      if (!d || !d.is_active) throw notFound();
      return d;
    },
  });

export const Route = createFileRoute("/community/dealers/$slug")({
  loader: ({ params, context }) => context.queryClient.ensureQueryData(dOptions(params.slug)),
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Dealer unavailable — BidNova Auctions" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const d = loaderData;
    const url = `https://bidnovaauctions.uk/community/dealers/${params.slug}`;
    const loc = [d.town || d.city, d.county, d.country].filter(Boolean).join(", ");
    const desc =
      d.description?.slice(0, 155) ??
      `${d.name}${loc ? ` — ${loc}.` : "."} Listed in the BidNova UK dealer directory.`;
    return {
      meta: [
        { title: `${d.name} — UK Card Dealer | BidNova Community` },
        { name: "description", content: desc },
        { property: "og:title", content: d.name },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...(d.logo_url
          ? [
              { property: "og:image", content: d.logo_url },
              { name: "twitter:image", content: d.logo_url },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: d.name,
            description: d.description ?? undefined,
            image: d.logo_url ?? undefined,
            url: d.website_url ?? url,
            telephone: d.phone ?? undefined,
            email: d.email ?? undefined,
            address: {
              "@type": "PostalAddress",
              streetAddress: d.address ?? undefined,
              addressLocality: d.town ?? d.city ?? undefined,
              addressRegion: d.county ?? undefined,
              postalCode: d.postcode ?? undefined,
              addressCountry: d.country,
            },
            geo:
              d.latitude != null && d.longitude != null
                ? { "@type": "GeoCoordinates", latitude: d.latitude, longitude: d.longitude }
                : undefined,
            sameAs: [d.website_url, d.facebook_url, d.instagram_url, d.discord_url].filter(Boolean),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Community", item: "https://bidnovaauctions.uk/community" },
              { "@type": "ListItem", position: 3, name: "Dealers", item: "https://bidnovaauctions.uk/community/dealers" },
              { "@type": "ListItem", position: 4, name: d.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: DealerDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function DealerDetail() {
  const { t } = useTranslation("community");
  const { slug } = Route.useParams();
  const { data: d } = useSuspenseQuery(dOptions(slug));

  const address = [d.address, d.town || d.city, d.county, d.postcode, d.country]
    .filter(Boolean)
    .join(", ");
  const mapsQuery = encodeURIComponent(address || d.name);

  return (
    <article className="mx-auto max-w-5xl px-6 sm:px-8 py-14">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">{t("layout.breadcrumbHome", "Home")}</Link>
        <span className="mx-2">/</span>
        <Link to="/community" className="hover:text-primary">{t("layout.breadcrumbCommunity", "Community")}</Link>
        <span className="mx-2">/</span>
        <Link to="/community/dealers" className="hover:text-primary">{t("nav.dealers", "Dealers")}</Link>
        <span className="mx-2">/</span>
        <span className="text-white/80 line-clamp-1">{d.name}</span>
      </nav>

      {d.banner_url && (
        <div className="mb-8 aspect-[3/1] overflow-hidden rounded-3xl border border-white/10 bg-black/40">
          <SmartImage src={d.banner_url} alt={`${d.name} storefront banner`} width={1280} height={360} widths={[640, 960, 1280, 1600]} sizes="100vw" priority className="h-full w-full object-cover" />
        </div>
      )}

      <header className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="grid h-20 w-20 place-items-center rounded-2xl border border-white/10 bg-black/40 overflow-hidden shrink-0">
          {d.logo_url ? (
            <SmartImage src={d.logo_url} alt={`${d.name} logo`} width={80} height={80} widths={[80, 160]} className="h-full w-full object-cover" />
          ) : (
            <Store className="h-8 w-8 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl text-white">{d.name}</h1>
            {d.is_featured && (
              <span className="rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[11px] text-gold">
                {t("dealers.detail.featured", "Featured")}
              </span>
            )}
          </div>
          {(d.town || d.city || d.county) && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              {[d.town || d.city, d.county, d.country].filter(Boolean).join(", ")}
            </p>
          )}
          {d.categories.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {d.categories.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[11px] text-muted-foreground"
                >
                  {DEALER_CATEGORY_LABELS[c] ?? c}
                </span>
              ))}
              {d.supported_games.map((g) => (
                <span
                  key={g}
                  className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {d.description && (
        <div className="mt-8 whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-white/90 leading-relaxed">
          {d.description}
        </div>
      )}

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
          <h2 className="font-display text-lg text-white">{t("dealers.detail.contact", "Contact")}</h2>
          <ul className="mt-4 space-y-2.5 text-sm">
            {d.website_url && (
              <ContactRow icon={Globe} href={d.website_url}>{t("dealers.detail.website", "Website")}</ContactRow>
            )}
            {d.email && (
              <ContactRow icon={Mail} href={`mailto:${d.email}`}>{d.email}</ContactRow>
            )}
            {d.phone && (
              <ContactRow icon={Phone} href={`tel:${d.phone.replace(/\s+/g, "")}`}>
                {d.phone}
              </ContactRow>
            )}
            {d.facebook_url && (
              <ContactRow icon={Facebook} href={d.facebook_url}>Facebook</ContactRow>
            )}
            {d.instagram_url && (
              <ContactRow icon={Instagram} href={d.instagram_url}>Instagram</ContactRow>
            )}
            {address && (
              <ContactRow
                icon={MapPin}
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
              >
                {address}
              </ContactRow>
            )}
          </ul>
        </div>

        {d.opening_hours && Object.keys(d.opening_hours).length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
            <h2 className="flex items-center gap-2 font-display text-lg text-white">
              <Clock className="h-4 w-4 text-primary" aria-hidden /> {t("dealers.detail.openingHours", "Opening Hours")}
            </h2>
            <ul className="mt-4 space-y-1.5 text-sm">
              {DAY_ORDER.map((day) => {
                const val = d.opening_hours?.[day];
                if (!val) return null;
                return (
                  <li key={day} className="flex items-center justify-between border-b border-white/5 pb-1.5 last:border-b-0">
                    <span className="capitalize text-muted-foreground">{day}</span>
                    <span className="text-white/90">{val}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-12">
        <Link
          to="/community/dealers"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> {t("dealers.detail.backLink", "Back to directory")}
        </Link>
      </div>
    </article>
  );
}

function ContactRow({
  icon: Icon,
  href,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  children: React.ReactNode;
}) {
  const external = /^https?:\/\//i.test(href);
  return (
    <li>
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="flex items-center gap-2.5 text-white/90 hover:text-primary transition-colors"
      >
        <Icon className="h-4 w-4 text-primary shrink-0" aria-hidden />
        <span className="truncate">{children}</span>
        {external && <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />}
      </a>
    </li>
  );
}
